/* eslint-disable array-bracket-newline */
/**
 * Info tables
 */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AutoSizer } from 'react-virtualized';
import { useAccount } from 'wagmi';
import MyPositions from './MyPositions';
import PriceAlerts from './PriceAlerts';
import LimitOrders from './LimitOrders';
import TopTraders from './TopTraders';
import Trades from './Trades';
import OrdersHistory from './OrdersHistory';
import Text from '../../atoms/Text';
import OpenOrders from './OpenOrders';
import WalletModal from '../Modal/WalletModal';
import { getWatchedLimitOrders } from '../../selectors/limitOrders';
import { getWatchedPriceAlerts } from '../../selectors/priceAlerts';
import { actionSetTradesWallet } from '../../actions/dex_trades';
import { IStore } from '../../reducers';
import { CEX_TABS, DEX_TABS, TradesTab } from './helpers';

import './styles.scss';

export const getClassNameForRow = (isActive: boolean, isBuying: boolean) => {
  if (isActive) {
    return isBuying ? 'row--green' : 'row--red';
  }
  return '';
};

const PriceAlertsCounter = () => {
  const watchedAlerts = useSelector(getWatchedPriceAlerts);

  return (
    <div className='dex_trades__options__item__counter_tab'>
      <Text size={10} className='text--grey-600'>
        {watchedAlerts.length}
      </Text>
    </div>
  );
};

const LimitOrdersCounter = () => {
  const watchedLimitOrders = useSelector(getWatchedLimitOrders);

  return (
    <div className='dex_trades__options__item__counter_tab'>
      <Text size={10} className='text--grey-600'>
        {watchedLimitOrders.length}
      </Text>
    </div>
  );
};

interface ITradesTabProps {
  cex: boolean;
  active: TradesTab;
  onChange(tab: TradesTab): void;
}

const Tabs = ({ cex, active, onChange }: ITradesTabProps) => (
  <div className='dex_trades__options'>
    {(cex ? CEX_TABS : DEX_TABS).map((tab) => (
      <div
        key={tab.name}
        onClick={() => onChange(tab.name)}
        className={`dex_trades__options__item ${
          tab.name === active ? 'dex_trades__options__item--active' : ''
        }`}
        data-qa={tab.name}
      >
        <div className={`dex_trades__options__item__icon ${tab.icon}`} />
        <Text size={12}>{tab.name}</Text>
        {tab.name === TradesTab.PriceAlerts && <PriceAlertsCounter />}
        {tab.name === TradesTab.LimitOrders && <LimitOrdersCounter />}
      </div>
    ))}
  </div>
);

interface IDexTradesProps {
  cex: boolean;
}

/**
 * Component for uniswap and its forks
 * @param props
 * @returns
 */
const DexTrades = ({ cex }: IDexTradesProps) => {
  const [tab, setTab] = useState<TradesTab>(TradesTab.None);
  const [walletWindow, setWalletWindow] = useState<any>();

  const id = useSelector((state: IStore) => state.current.id);

  const dispatch = useDispatch();
  const { address: currentWallet } = useAccount();

  useEffect(() => {
    setTab(cex ? TradesTab.CEXOrdersOpen : TradesTab.Trades);
  }, [cex]);

  // Set trades wallet to user one if pair or wallet changed
  useEffect(() => {
    dispatch(actionSetTradesWallet(currentWallet || null));
    setWalletWindow(undefined);
  }, [id, dispatch]);

  // Show wallet modal inside trades widget
  const openWalletModal = useCallback((props: any) => {
    setWalletWindow(props);
  }, []);

  if (walletWindow) {
    return (
      <div className='dex_trades dex_trades--modal'>
        <WalletModal
          windowProps={walletWindow}
          actionCloseModal={() => setWalletWindow(undefined)}
        />
      </div>
    );
  }

  return (
    <div className='dex_trades'>
      <Tabs cex={cex} active={tab} onChange={setTab} />
      <div className='dex_trades__separator' />
      {tab == TradesTab.CEXOrdersHistory && (
        <AutoSizer style={{ width: '100%', height: '100%' }}>
          {({ width }) => <OrdersHistory width={width} />}
        </AutoSizer>
      )}
      {tab == TradesTab.CEXOrdersOpen && (
        <AutoSizer style={{ width: '100%', height: '100%' }}>
          {({ width }) => <OpenOrders width={width} />}
        </AutoSizer>
      )}
      {tab === TradesTab.Trades && (
        <AutoSizer style={{ width: '100%', height: '100%' }}>
          {({ width }) => (
            <Trades
              width={width}
              currentWallet={currentWallet}
              openWalletModal={openWalletModal}
            />
          )}
        </AutoSizer>
      )}
      {tab === TradesTab.Positions && (
        <AutoSizer style={{ width: '100%', height: '100%' }}>
          {({ width }) => <MyPositions width={width} />}
        </AutoSizer>
      )}
      {tab === TradesTab.TopTraders && (
        <AutoSizer style={{ width: '100%', height: '100%' }}>
          {({ width }) => (
            <TopTraders
              width={width}
              currentWallet={currentWallet}
              openWalletModal={openWalletModal}
            />
          )}
        </AutoSizer>
      )}
      {tab === TradesTab.PriceAlerts && (
        <AutoSizer style={{ width: '100%', height: '100%' }}>
          {({ width }) => <PriceAlerts width={width} />}
        </AutoSizer>
      )}
      {tab === TradesTab.LimitOrders && (
        <AutoSizer style={{ width: '100%', height: '100%' }}>
          {({ width }) => <LimitOrders width={width} />}
        </AutoSizer>
      )}
    </div>
  );
};

export default DexTrades;
